import { z } from "zod";
import { nullishToOptional } from "../../helpers";
import { DesignTokenType } from "../elements";
import { ImportWarning } from "../import/warning";

export const FileStructureStats = z.object({
  frames: zeroNumberByDefault(),
  components: zeroNumberByDefault(),
  componentSets: zeroNumberByDefault(),
});

const SourceImportSummaryByTokenTypeKey = DesignTokenType.or(
  // Backward compatibility
  z.enum(["Measure", "Radius", "GenericToken", "Font", "Text"])
);

export const SourceImportSummaryByTokenType = z.record(SourceImportSummaryByTokenTypeKey, z.number());

export const SourceImportTokenSummary = z.object({
  tokensCreated: zeroNumberByDefault(),
  tokensUpdated: zeroNumberByDefault(),
  tokensDeleted: zeroNumberByDefault(),
  tokensCreatedPerType: SourceImportSummaryByTokenType.nullish().transform(v => v ?? {}),
  tokensUpdatedPerType: SourceImportSummaryByTokenType.nullish().transform(v => v ?? {}),
  tokensDeletedPerType: SourceImportSummaryByTokenType.nullish().transform(v => v ?? {}),
});

export const SourceImportComponentSummary = z.object({
  componentsCreated: zeroNumberByDefault(),
  componentsUpdated: zeroNumberByDefault(),
  componentsDeleted: zeroNumberByDefault(),
  componentAssetsCreated: zeroNumberByDefault(),
  componentAssetsUpdated: zeroNumberByDefault(),
  componentAssetsDeleted: zeroNumberByDefault(),
});

export const SourceImportStorybookSummary = z.object({
  created: zeroNumberByDefault(),
  updated: zeroNumberByDefault(),
  deleted: zeroNumberByDefault(),
});

export const SourceImportFrameSummary = z.object({
  assetsInFile: nullishToOptional(FileStructureStats.optional()),
  invalidReferencesCount: nullishToOptional(z.number().optional()),
});

export const SourceImportSummary = z.object({
  sourceId: nullishToOptional(z.string()),
  brandId: nullishToOptional(z.string()),
  versionId: nullishToOptional(z.string()),
  error: nullishToOptional(z.any()),
  isFailed: z.boolean(),
  warnings: z
    .array(ImportWarning)
    .nullish()
    .transform(v => v ?? []),

  ...SourceImportTokenSummary.shape,
  ...SourceImportComponentSummary.shape,
  ...FileStructureStats.shape,
});

export type FileStructureStats = z.infer<typeof FileStructureStats>;
export type SourceImportSummaryByTokenType = z.infer<typeof SourceImportSummaryByTokenType>;
export type SourceImportTokenSummary = z.infer<typeof SourceImportTokenSummary>;
export type SourceImportComponentSummary = z.infer<typeof SourceImportComponentSummary>;
export type SourceImportFrameSummary = z.infer<typeof SourceImportFrameSummary>;
export type SourceImportSummary = z.infer<typeof SourceImportSummary>;
export type SourceImportStorybookSummary = z.infer<typeof SourceImportStorybookSummary>;

function zeroNumberByDefault() {
  return z
    .number()
    .nullish()
    .transform(v => v ?? 0);
}
